import { TwoFactorServiceInstance } from '@/services/twoFactorService'

const initState = () => {
    return {
        two_factor_enabled: false,
        two_factor_confirmed: false,
        two_factor_requires_challenge: false,
        two_factor_recovery_codes_count: 0,

        // Datos de configuración
        qrCode: null,
        secretKey: null,
        recoveryCodes: [],

        // Estados de UI
        isLoading: false,
        isModalVisible: false,
        challengeRequired: false,

        // Mensajes y errores
        message: null,
        error: null,
        messageType: 'info', // 'success', 'error', 'warning', 'info'

        // Configuración del modal
        modalConfig: {
            title: '',
            type: 'challenge', // 'challenge', 'setup', 'disable'
            onSuccess: null,
            onError: null,
            showRecoveryCodes: false,
        },
    }
}

const getters = {
    // Estado principal
    twoFactorEnabled: state => state.two_factor_enabled,
    twoFactorConfirmed: state => state.two_factor_confirmed,
    needsSetup: state => state.two_factor_enabled && !state.two_factor_confirmed,
    needsChallenge: state => state.two_factor_requires_challenge,

    // Datos
    qrCodeSvg: state => state.qrCode,
    secretKey: state => state.secretKey,
    recoveryCodes: state => state.recoveryCodes,
    two_factor_recovery_codes_count: state => state.two_factor_recovery_codes_count,

    // Estados de UI
    isProcessing: state => state.isLoading,
    currentMessage: state => state.message,
    currentError: state => state.error,
    messageType: state => state.messageType,

    // Modal
    showModal: state => state.isModalVisible,
    modalConfiguration: state => state.modalConfig,

    // Estado completo para componentes
    twoFactorStatus: state => ({
        enabled: state.two_factor_enabled,
        confirmed: state.two_factor_confirmed,
        needsSetup: state.two_factor_enabled && !state.two_factor_confirmed,
        needsChallenge: state.two_factor_requires_challenge,
        two_factor_recovery_codes_count: state.two_factor_recovery_codes_count,
    }),
}

const mutations = {
    // Mutaciones de estado
    SET_TWO_FACTOR_STATUS(state, status) {
        state.two_factor_enabled = status.two_factor_enabled || false
        state.two_factor_confirmed = status.two_factor_confirmed || false
        state.two_factor_requires_challenge = status.two_factor_requires_challenge || false
        state.two_factor_recovery_codes_count = status.two_factor_recovery_codes_count || 0
    },

    SET_LOADING(state, loading) {
        state.isLoading = loading
    },

    SET_QR_DATA(state, data) {
        state.qrCode = data.qr_code || null
        state.secretKey = data.secret_key || data.manual_entry_key || null
    },

    SET_RECOVERY_CODES(state, codes) {
        state.recoveryCodes = codes || []
        state.two_factor_recovery_codes_count = codes ? codes.length : 0
    },

    SET_MESSAGE(state, { message, type = 'info' }) {
        state.message = message
        state.messageType = type
        state.error = type === 'error' ? message : null
    },

    CLEAR_MESSAGE(state) {
        state.message = null
        state.error = null
        state.messageType = 'info'
    },

    SET_CHALLENGE_REQUIRED(state, required) {
        state.challengeRequired = required
        state.two_factor_requires_challenge = required
    },

    // Mutaciones del modal
    SET_MODAL_VISIBLE(state, visible) {
        state.isModalVisible = visible
    },

    SET_MODAL_CONFIG(state, config) {
        state.modalConfig = { ...state.modalConfig, ...config }
    },

    RESET_MODAL(state) {
        state.isModalVisible = false
        state.modalConfig = {
            title: '',
            type: 'challenge',
            onSuccess: null,
            onError: null,
            showRecoveryCodes: false,
        }
    },

    // Reset completo
    RESET_TWO_FACTOR_STATE(state) {
        state.two_factor_enabled = false
        state.two_factor_confirmed = false
        state.two_factor_requires_challenge = false
        state.two_factor_recovery_codes_count = 0
        state.qrCode = null
        state.secretKey = null
        state.recoveryCodes = []
        state.challengeRequired = false
    },
}

const actions = {
    // Cargar estado inicial
  async fetchTwoFactorStatus({ commit }) {
    commit('SET_LOADING', true)
    commit('CLEAR_MESSAGE')

    try {
      const result = await TwoFactorServiceInstance.getStatus()
      
      if (result.success) {
        commit('SET_TWO_FACTOR_STATUS', result.data)
        return result.data
      } else {
        commit('SET_MESSAGE', { message: result.message, type: 'error' })
        throw new Error(result.message)
      }
    } catch (error) {
      commit('SET_MESSAGE', { message: 'Error al cargar el estado de 2FA', type: 'error' })
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  // Verificar si necesita challenge
  async checkChallengeRequired({ commit }) {
    try {
      const result = await TwoFactorServiceInstance.needsChallenge()
      
      if (result.success) {
        commit('SET_CHALLENGE_REQUIRED', result.data.needs_challenge)
        return result.data
      }
      
      return { needs_challenge: false }
    } catch (error) {
      console.error('Error checking 2FA challenge:', error)
      return { needs_challenge: false }
    }
  },

  // Habilitar 2FA
  async enableTwoFactor({ commit, dispatch }, password) {
    commit('SET_LOADING', true)
    commit('CLEAR_MESSAGE')

    try {
      const result = await TwoFactorServiceInstance.enable(password)
      
      if (result.success) {
        commit('SET_QR_DATA', result.data)
        if (result.data.recovery_codes) {
          commit('SET_RECOVERY_CODES', result.data.recovery_codes)
        }
        commit('SET_MESSAGE', { message: result.message, type: 'success' })
        
        // Actualizar estado
        await dispatch('fetchTwoFactorStatus')
        return result.data
      } else {
        commit('SET_MESSAGE', { message: result.message, type: 'error' })
        throw new Error(result.message)
      }
    } catch (error) {
      commit('SET_MESSAGE', { message: error.message || 'Error al habilitar 2FA', type: 'error' })
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  // Confirmar configuración inicial
  async confirmTwoFactor({ commit, dispatch }, code) {
    commit('SET_LOADING', true)
    commit('CLEAR_MESSAGE')

    try {
      const result = await TwoFactorServiceInstance.confirm(code)
      
      if (result.success) {
        commit('SET_MESSAGE', { message: result.message, type: 'success' })
        
        // Actualizar estado
        await dispatch('fetchTwoFactorStatus')
        return result.data
      } else {
        commit('SET_MESSAGE', { message: result.message, type: 'error' })
        throw new Error(result.message)
      }
    } catch (error) {
      commit('SET_MESSAGE', { message: error.message || 'Código de verificación inválido', type: 'error' })
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  // Challenge de 2FA
  async challengeTwoFactor({ commit }, code) {
    commit('SET_LOADING', true)
    commit('CLEAR_MESSAGE')

    try {
      const result = await TwoFactorServiceInstance.challenge(code)
      
      if (result.success) {
        commit('SET_CHALLENGE_REQUIRED', false)
        commit('SET_MESSAGE', { message: result.message, type: 'success' })
        return result.data
      } else {
        commit('SET_MESSAGE', { message: result.message, type: 'error' })
        throw new Error(result.message)
      }
    } catch (error) {
      commit('SET_MESSAGE', { message: error.message || 'Código de verificación inválido', type: 'error' })
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  // Deshabilitar 2FA
  async disableTwoFactor({ commit, dispatch }, password) {
    commit('SET_LOADING', true)
    commit('CLEAR_MESSAGE')

    try {
      const result = await TwoFactorServiceInstance.disable(password)
      
      if (result.success) {
        commit('RESET_TWO_FACTOR_STATE')
        commit('SET_MESSAGE', { message: result.message, type: 'info' })
        
        // Actualizar estado
        await dispatch('fetchTwoFactorStatus')
        return result.data
      } else {
        commit('SET_MESSAGE', { message: result.message, type: 'error' })
        throw new Error(result.message)
      }
    } catch (error) {
      commit('SET_MESSAGE', { message: error.message || 'Error al deshabilitar 2FA', type: 'error' })
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  // Obtener códigos QR
  async getQRCode({ commit }) {
    commit('SET_LOADING', true)

    try {
      const result = await TwoFactorServiceInstance.getQRCode()
      
      if (result.success) {
        commit('SET_QR_DATA', result.data)
        return result.data
      } else {
        commit('SET_MESSAGE', { message: result.message, type: 'error' })
        throw new Error(result.message)
      }
    } catch (error) {
      commit('SET_MESSAGE', { message: 'Error al obtener código QR', type: 'error' })
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  // Generar nuevos códigos de recuperación
  async generateRecoveryCodes({ commit }) {
    commit('SET_LOADING', true)
    commit('CLEAR_MESSAGE')

    try {
      const result = await TwoFactorServiceInstance.generateRecoveryCodes()
      
      if (result.success) {
        commit('SET_RECOVERY_CODES', result.data.recovery_codes)
        commit('SET_MESSAGE', { message: result.message, type: 'success' })
        return result.data.recovery_codes
      } else {
        commit('SET_MESSAGE', { message: result.message, type: 'error' })
        throw new Error(result.message)
      }
    } catch (error) {
      commit('SET_MESSAGE', { message: 'Error al generar códigos de recuperación', type: 'error' })
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  // Acciones del modal
  showTwoFactorModal({ commit }, config = {}) {
    commit('SET_MODAL_CONFIG', config)
    commit('SET_MODAL_VISIBLE', true)
  },

  hideTwoFactorModal({ commit }) {
    commit('SET_MODAL_VISIBLE', false)
    commit('RESET_MODAL')
  },

  // Limpiar mensajes
  clearMessages({ commit }) {
    commit('CLEAR_MESSAGE')
  },

  // Inicialización completa
  async initializeTwoFactor({ dispatch }) {
    try {
      await dispatch('fetchTwoFactorStatus')
      await dispatch('checkChallengeRequired')
    } catch (error) {
      console.error('Error initializing 2FA:', error)
    }
  },
}

export const TwoFactor = {
    namespaced: true,
    state: initState,
    getters,
    actions,
    mutations,
}